import { z } from 'zod'
import {
  INT8_MAX,
  decimalFromScaled,
  divideRounded,
  scaledFromDecimal,
} from '#model/support/decimal'
import { DomainError, ERROR, ISSUE } from '#model/support/errors'
import { MINOR_EXPONENT, currencySchema, exactFigures, formatInCurrency } from './money'
import type { Currency, Money } from './money'

export const baseUnitSchema = z.enum(['kg', 'l', 'piece'])
export type BaseUnit = z.infer<typeof baseUnitSchema>

export const unitSchema = z.enum(['g', 'kg', 'ml', 'l', 'piece'])
export type Unit = z.infer<typeof unitSchema>

const BASE_OF: Record<Unit, BaseUnit> = {
  g: 'kg',
  kg: 'kg',
  ml: 'l',
  l: 'l',
  piece: 'piece',
}

const MILLI_PER_UNIT: Record<Unit, bigint> = {
  g: 1n,
  kg: 1000n,
  ml: 1n,
  l: 1000n,
  piece: 1000n,
}

export interface Quantity {
  readonly milli: bigint
  readonly unit: BaseUnit
}

const quantityFields = z.object({
  milli: z.bigint().positive().max(INT8_MAX),
  unit: baseUnitSchema,
})

export const quantitySchema = quantityFields.refine(
  (quantity) => quantity.unit !== 'piece' || quantity.milli % 1000n === 0n,
  {
    error: ISSUE.QUANTITY_FRACTIONAL_PIECE,
  },
)
/**
 * Precision below the resolution of the unit is refused, not dropped: truncating made
 * «1,500» г — how a Russian keyboard writes 1500 — into 1 g, a thousandfold error.
 */
function quantityFromDecimal(input: string, unit: Unit): Quantity | null {
  const thousandths = scaledFromDecimal(input, 3)
  if (thousandths === null) return null

  const scaled = thousandths * MILLI_PER_UNIT[unit]
  if (scaled % 1000n !== 0n) return null

  const milli = scaled / 1000n
  if (milli <= 0n || milli > INT8_MAX) return null

  if (BASE_OF[unit] === 'piece' && milli % 1000n !== 0n) return null

  return { milli, unit: BASE_OF[unit] }
}

export function parseQuantity(input: string, unit: Unit): Quantity {
  const quantity = quantityFromDecimal(input, unit)
  if (quantity === null) throw new DomainError(ERROR.INVALID_QUANTITY, input)
  return quantity
}

export function decimalFromMilli({ milli }: Quantity): `${number}` {
  return decimalFromScaled(milli, 3)
}

export const quantityWireSchema = z.object({
  value: z.string().max(40),
  unit: baseUnitSchema,
})
export type QuantityWire = z.infer<typeof quantityWireSchema>

export const quantityCodec = z.codec(quantityWireSchema, quantitySchema, {
  decode: ({ value, unit }, payload) => {
    const quantity = quantityFromDecimal(value, unit)
    if (quantity === null) {
      payload.issues.push({
        code: 'custom',
        input: value,
        path: ['value'],
        message: ERROR.INVALID_QUANTITY,
      })
      return { milli: 0n, unit }
    }
    return quantity
  },
  encode: (quantity) => ({ value: decimalFromMilli(quantity), unit: quantity.unit }),
})

export const UNIT_PRICE_DIGITS = 6
export const UNIT_PRICE_SCALE = 10n ** BigInt(UNIT_PRICE_DIGITS)

export interface UnitPrice {
  readonly scaledMinor: bigint
  readonly currency: Currency
  readonly unit: BaseUnit
}

export function unitPrice(amount: Money, quantity: Quantity): UnitPrice {
  if (quantity.milli <= 0n) {
    throw new DomainError(ERROR.INVALID_QUANTITY, String(quantity.milli))
  }
  if (amount.minor < 0n) {
    throw new DomainError(ERROR.INVALID_AMOUNT, String(amount.minor))
  }
  const scaledMinor = divideRounded(amount.minor * 1000n * UNIT_PRICE_SCALE, quantity.milli)
  return { scaledMinor, currency: amount.currency, unit: quantity.unit }
}

export function compareUnitPrice(a: UnitPrice, b: UnitPrice): number {
  if (a.currency !== b.currency) {
    throw new DomainError(ERROR.CURRENCY_MISMATCH, `${a.currency} vs ${b.currency}`)
  }
  if (a.unit !== b.unit) {
    throw new DomainError(ERROR.UNIT_MISMATCH, `${a.unit} vs ${b.unit}`)
  }
  if (a.scaledMinor === b.scaledMinor) return 0
  return a.scaledMinor < b.scaledMinor ? -1 : 1
}

/**
 * The amount of a unit price, by the rule money is printed by (`exactFigures`, MOL-205) with at least
 * two digits whatever the currency keeps: the point is telling 570 from 577,78.
 *
 * Only the amount: the unit is a word of the interface («л», «шт»), not of the domain, so the
 * screen names it. The first version printed the unit's code and the currency's ISO symbol —
 * «577,78 AMD/l» where the shelf shows «577,78 ֏/л» (MOL-24).
 */
export function formatUnitPrice(price: UnitPrice, locale = 'ru-RU'): string {
  const exponent = Math.max(MINOR_EXPONENT[price.currency], 2)
  const major = decimalFromScaled(
    price.scaledMinor,
    UNIT_PRICE_DIGITS + MINOR_EXPONENT[price.currency],
  )
  return formatInCurrency(major, price.currency, locale, exactFigures(exponent))
}

/**
 * How much of a thing, as the screen prints it: «1», «0,9», «1,128». Trailing zeros go — the
 * quantity is exact to a thousandth, and «1,000 л» reads as a precision nobody measured (MOL-22).
 *
 * Only the number, as with a unit price: the unit is a word of the interface, not of the domain.
 */
export function formatQuantity(quantity: Quantity, locale = 'ru-RU'): string {
  // Thousandths, the scale a quantity is stored in (`decimalFromMilli`).
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(
    decimalFromMilli(quantity),
  )
}

/**
 * A unit price crosses the wire as a decimal in major units, with every digit the ratio holds —
 * the same way money does, and for the same reason: the screen rounds on output only, and a
 * price rounded here would compare 577,78 against 577,78 where the rows differ in the third
 * place (MOL-21).
 *
 * **No int8 ceiling**, unlike money. A unit price is a ratio that is never stored, and it keeps
 * its own scale: 92 233 720,37 ֏ for one gram is a legal amount over a legal quantity whose price
 * per kilo is past int8. The first version capped it here, and since the answer is encoded after
 * the expense is written, such a row was stored and every later read of its trip answered 500
 * (MOL-21, adversarial А). The string bound is what keeps the wire finite.
 */
const unitPriceFields = z.object({
  scaledMinor: z.bigint().nonnegative(),
  currency: currencySchema,
  unit: baseUnitSchema,
})

export const unitPriceWireSchema = z.object({
  amount: z.string().max(60),
  currency: currencySchema,
  unit: baseUnitSchema,
})
export type UnitPriceWire = z.infer<typeof unitPriceWireSchema>

const unitPriceDigits = (currency: Currency): number => UNIT_PRICE_DIGITS + MINOR_EXPONENT[currency]

export const unitPriceCodec = z.codec(unitPriceWireSchema, unitPriceFields, {
  decode: ({ amount, currency, unit }, payload) => {
    const scaledMinor = scaledFromDecimal(amount, unitPriceDigits(currency))
    if (scaledMinor === null || scaledMinor < 0n) {
      payload.issues.push({
        code: 'custom',
        input: amount,
        path: ['amount'],
        message: ERROR.INVALID_AMOUNT,
      })
      return { scaledMinor: 0n, currency, unit }
    }
    return { scaledMinor, currency, unit }
  },
  encode: (price) => ({
    amount: decimalFromScaled(price.scaledMinor, unitPriceDigits(price.currency)),
    currency: price.currency,
    unit: price.unit,
  }),
})
